import classNames from 'classnames';
import { useState } from 'react';
import type { JSX } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { useFormatMessage } from '@alltrails/core/hooks/useFormatMessage';
import Dropdown from '@alltrails/denali/components/Dropdown';
import DropdownMenu from '@alltrails/denali/components/DropdownMenu';
import IconButton from '@alltrails/denali/components/IconButton';
import RadioGroup from '@alltrails/denali/components/RadioGroup';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import Sort from '@alltrails/denali/icons/Sort';
import type { DropdownMenuOption } from '@alltrails/denali/types';
import { Modal } from '@alltrails/modal';
import type { SortOption } from '../../types/Reviews';
import styles from './ReviewsSort.module.scss';

const messages = defineMessages({
  SORT_BY: { defaultMessage: 'Sort by' },
  AT_SORT: { defaultMessage: 'AllTrails sort' },
  DESC: { defaultMessage: 'Newest' },
  ASC: { defaultMessage: 'Oldest' },
  HIGHEST_RATED: { defaultMessage: 'Highest rating' },
  LOWEST_RATED: { defaultMessage: 'Lowest rating' }
});

export type ReviewsSortProps = {
  className?: string;
  sortOption: SortOption;
  sortOptions?: SortOption[];
  onSortChanged: (newSort: SortOption) => void;
  sticky?: boolean;
};

const ReviewsSort = ({
  className,
  sortOption,
  sortOptions = ['atSort', 'desc', 'asc', 'highestRated', 'lowestRated'],
  onSortChanged,
  sticky
}: ReviewsSortProps): JSX.Element => {
  const isMobile = useIsMobileSizedScreen();
  const [isOpen, setIsOpen] = useState(false);

  const {
    formattedDefaultMessages: { SORT_BY, AT_SORT, DESC, ASC, HIGHEST_RATED, LOWEST_RATED }
  } = useFormatMessage(messages);

  const optionLabels: Record<SortOption, string> = {
    atSort: AT_SORT,
    desc: DESC,
    asc: ASC,
    highestRated: HIGHEST_RATED,
    lowestRated: LOWEST_RATED
  };

  if (isMobile) {
    const radioOptions = sortOptions.map(key => ({
      testId: `reviewsSort-radio-${key}`,
      text: optionLabels[key],
      value: key,
      selected: sortOption === key
    }));

    return (
      <div className={classNames(styles.labelAndSelect, className, { [styles.sticky]: sticky })}>
        <IconButton icon={{ Component: Sort }} onClick={() => setIsOpen(true)} size="sm" testId="reviewsSort-mobile" title={SORT_BY} variant="flat" />
        <Modal
          modalClassName={styles.sortBottomModal}
          isOpen={isOpen}
          onCloseRequest={() => setIsOpen(false)}
          size="sm"
          title={<FormattedMessage defaultMessage="Sort reviews" />}
          testId="reviewsSort-mobile-modal"
        >
          <RadioGroup
            name="reviewsSort"
            onChange={value => {
              onSortChanged(value as SortOption);
              setIsOpen(false);
            }}
            options={radioOptions}
          />
        </Modal>
      </div>
    );
  } else {
    const dropdownOptions: DropdownMenuOption[] = sortOptions.map(key => ({
      testId: `reviewsSort-dropdown-${key}`,
      title: optionLabels[key],
      selected: sortOption === key,
      onClick: () => {
        onSortChanged(key);
        setIsOpen(false);
      }
    }));

    return (
      <div className={classNames(styles.labelAndSelect, className, { [styles.sticky]: sticky })}>
        <DropdownMenu
          anchor={
            <Dropdown
              className={classNames(styles.sortButton, { [styles.dropdownOpen]: isOpen })}
              leadingIcon={{ Component: Sort }}
              text={optionLabels[sortOption]}
              variant="flat"
              size="sm"
              onClick={() => setIsOpen(!isOpen)}
              testId="reviewsSort-desktop"
              title={SORT_BY}
              aria-expanded={isOpen}
              aria-haspopup="menu"
            />
          }
          className={styles.dropdown}
          isOpen={isOpen}
          onCloseRequest={() => setIsOpen(false)}
          options={dropdownOptions}
          placement="bottom-end"
        />
      </div>
    );
  }
};

export default ReviewsSort;
